import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Flex, Button, useDisclosure, Alert, AlertIcon, AlertDescription, Box, Text } from 'src/components/ui';
import CustomTooltip from 'src/components/common/Tooltip';
import PublishingConfirmationModal, { type PublishingModalStep } from './parts/PublishingConfirmationModal';
import InsufficientCreditsModal from './parts/InsufficientCreditsModal';
import CampaignLimitModal from './parts/CampaignLimitModal';
import CampaignSuccessModal from './parts/CampaignSuccessModal';
import { CampaignContext } from 'src/contexts';
import { useMyIntegrations } from 'src/contexts/integration/IntegrationContext';
import { validateCampaignSchedule } from '../utils/validations';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	ECampaignScheduleType,
	IDesignDirection,
	ICreative,
	IPublishedCreative,
	IFailedCreative,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { processCallback } from 'src/lib/utils/processCallback';
import {
	publishCampaignChannels,
	createOrUpdateCampaign,
	genrateCampaignCreatives,
	getCampaign,
} from 'src/services/campaign';
import { delay } from 'src/lib/utils';
import { externalRedirect } from 'src/lib/utils/redirect';
import { checkCampaignCredits } from 'src/services/billing';
import { useForm } from 'react-hook-form';
import { customToast } from 'src/services/toast';

interface LaunchCampaignProps {
	isValidCampaignConfig: boolean;
	isValidCampaignBudget: boolean;
	selectedChannels: string[];
	designDirections: IDesignDirection[];
	creatives: ICreative[];
}

const LaunchCampaign: FC<LaunchCampaignProps> = ({
	isValidCampaignConfig,
	isValidCampaignBudget,
	selectedChannels = [],
	designDirections,
	creatives,
}) => {
	const {
		isOpen: isOpenPublishingConfirmationModal,
		onOpen: onOpenPublishingConfirmationModalBase,
		onClose: onClosePublishingConfirmationModalBase,
	} = useDisclosure();

	const onOpenPublishingConfirmationModal = () => {
		isModalOpenRef.current = true;
		setPublishResult(null);
		onOpenPublishingConfirmationModalBase();
	};
	const onClosePublishingConfirmationModal = () => {
		isModalOpenRef.current = false;
		onClosePublishingConfirmationModalBase();
	};
	const {
		isOpen: isOpenInsufficientCreditsModal,
		onOpen: onOpenInsufficientCreditsModal,
		onClose: onCloseInsufficientCreditsModal,
	} = useDisclosure();
	const {
		isOpen: isOpenCampaignLimitModal,
		onOpen: onOpenCampaignLimitModal,
		onClose: onCloseCampaignLimitModal,
	} = useDisclosure();
	const {
		isOpen: isOpenSuccessModal,
		onOpen: onOpenSuccessModal,
		onClose: onCloseSuccessModal,
	} = useDisclosure();
	const {
		campaign,
		setCampaign,
		setOpenSections,
		id: campaignId,
	} = useContext(CampaignContext);
	const { integrations } = useMyIntegrations();
	const { search, pathname } = useLocation();
	const navigate = useNavigate();
	const { handleSubmit } = useForm();
	const publishButtonRef = useRef<HTMLButtonElement | null>(null);
	const continueLaunchInitiatedRef = useRef(false);
	const [checkoutUrl, setCheckoutUrl] = useState<string>('');
	const [amountRequired, setAmountRequired] = useState<number>(0);
	const [campaignLimitData, setCampaignLimitData] = useState<{
		currentCount?: number;
		limit?: number;
		errorMessage?: string;
		provider?: 'appsumo' | 'stripe';
		planName?: string;
		canUpgrade?: boolean;
		upgradePrice?: number;
		upgradeCurrency?: string;
		nextPlanId?: string;
	}>({});
	const isDesignDirectionPending = designDirections?.some(
		(c) => c.status === 'pending' || c.status === 'generating',
	);
	const isCreativesPending = creatives?.some(
		(c) => c.status === 'pending' || c.status === 'generating',
	);
	const [isPublishButtonLoading, setIsPublishButtonLoading] = useState(false);
	const [publishingModalStep, setPublishingModalStep] = useState<PublishingModalStep>('checking-credits');
	const [publishResult, setPublishResult] = useState<{
		publishedCreatives: IPublishedCreative[];
		failedCreatives: IFailedCreative[];
	} | null>(null);
	const isModalOpenRef = useRef(false);
	const isLaunchingRef = useRef(false);
	const isPublishingRef = useRef(false);
	const isCampaignPublishing = campaign?.status === 'publishing';

	const areAllDesignDirectionsWithCreatives = () => {
		if (!designDirections || !creatives) return false;

		const creativeDesignDirectionIds = new Set(
			creatives.map((creative) => creative.designDirectionId),
		);

		return designDirections.every((direction) =>
			creativeDesignDirectionIds.has(direction.id),
		);
	};

	const getCampaignId = (): string | undefined => {
		return campaign?.id;
	};

	const isValidCampaignSchedule = useMemo(() => {
		if (!campaign?.schedule) return false;
		return validateCampaignSchedule(campaign.schedule);
	}, [campaign]);

	const handleInsufficientCreditsCancel = () => {
		onCloseInsufficientCreditsModal();
		setCheckoutUrl('');
		setAmountRequired(0);
	};

	const handleInsufficientCreditsContinue = () => {
		onCloseInsufficientCreditsModal();
		if (checkoutUrl) {
			externalRedirect(checkoutUrl);
		}
	};

	const validateCampaign = (): boolean => {
		if (!isValidCampaignConfig) {
			handleInvalidCampaignConfig();
			return false;
		}

		if (!isValidCampaignSchedule) {
			handleInvalidCampaignSchedule();
			return false;
		}

		if (!isValidCampaignBudget) {
			return false;
		}

		if (isOneOffScheduleWithoutEndDate()) {
			return false;
		}

		return true;
	};

	const handleInvalidCampaignConfig = () => {
		customToast(
			'Campaign configuration is incomplete. Please review and complete all required fields before publishing.',
			'error',
		);
	};

	const handleInvalidCampaignSchedule = () => {
		const scheduleFormSubmitElement = document.getElementById(
			'campaign-schedule-form-submit',
		);
		scheduleFormSubmitElement?.click();
	};

	const isOneOffScheduleWithoutEndDate = () => {
		return (
			campaign?.schedule?.type === ECampaignScheduleType['One-off'] &&
			!campaign.schedule.endDate
		);
	};

	const handlePublishClick = () => {
		if (!isValidCampaignConfig) {
			customToast(
				'Please complete the campaign configuration before publishing',
				'error',
			);
			return;
		}

		if (!isValidCampaignBudget) {
			customToast(
				'Please set up the campaign budget before publishing',
				'error',
			);
			return;
		}

		startLaunch();
	};

	// Check if we should continue launch after payment
	useEffect(() => {
		const urlParams = new URLSearchParams(search);
		const shouldContinueLaunch = urlParams.get('continueLaunch') === 'true';

		if (!shouldContinueLaunch) return;

		// Use sessionStorage to prevent duplicate publish across component remounts
		const storageKey = `continueLaunch_${campaignId || 'unknown'}`;
		if (sessionStorage.getItem(storageKey)) return;
		sessionStorage.setItem(storageKey, 'true');

		continueLaunchInitiatedRef.current = true;

		const resolvedCampaignId = campaignId || window.location.pathname.match(
			/\/projects\/campaigns\/([^/?]+)/,
		)?.[1];

		if (!resolvedCampaignId) {
			sessionStorage.removeItem(storageKey);
			customToast(
				'Error continuing launch process. Please try again manually.',
				'error',
			);
			return;
		}

		// Open the publishing modal in the current instance before any navigation
		// or async work, so the banner is guaranteed to render.
		setOpenSections([false, false, true]);
		setPublishingModalStep('publishing');
		onOpenPublishingConfirmationModal();

		// Mirror the sufficient-credits path so the inline "campaign is being
		// published" Alert appears and the Publish button stays disabled.
		if (campaign) {
			setCampaign({ ...campaign, status: 'publishing' });
			createOrUpdateCampaign({ status: 'publishing' }, resolvedCampaignId).catch(
				(error) => console.error('Error updating campaign status:', error),
			);
		}

		// Fire publish synchronously within this effect — a setTimeout here races
		// with any remount caused by navigate()/setOpenSections and can leave the
		// new instance with no modal state set.
		firePublish(resolvedCampaignId);
		sessionStorage.removeItem(storageKey);

		// Strip continueLaunch from the URL last, so the effect's re-run (with the
		// now-empty search) happens after the modal state has been queued.
		urlParams.delete('continueLaunch');
		const newSearch = urlParams.toString();
		navigate(`${pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
	}, [search]);

	const resolveRealCampaignId = async (): Promise<string | null> => {
		const realId = getCampaignId();
		if (realId && realId !== 'new') return realId;

		// Campaign was never saved — save it first to get a real ID
		if (campaign) {
			try {
				const saved = await createOrUpdateCampaign(campaign, 'new');
				if (saved?.id) {
					setCampaign(saved);
					return saved.id;
				}
			} catch (error) {
				console.error('Error saving campaign before publish:', error);
			}
		}
		return null;
	};

	const waitForCreatives = async (campaignIdForApi: string): Promise<boolean> => {
		let attemptCount = 0;

		while (attemptCount < 30) {
			const data = await getCampaign(campaignIdForApi);
			setCampaign(data);

			if (!data.creatives) return false;

			const freshDesignDirectionIds = new Set(
				(data.designDirections || []).map((d: any) => d.id),
			);
			const freshCreativeDirectionIds = new Set(
				data.creatives.map((c: any) => c.designDirectionId),
			);
			let allHaveCreatives = true;
			freshDesignDirectionIds.forEach((id) => {
				if (!freshCreativeDirectionIds.has(id)) allHaveCreatives = false;
			});
			const stillPending = data.creatives.some(
				(c: any) => c.status === 'pending' || c.status === 'generating',
			);

			if (allHaveCreatives && !stillPending) return true;

			attemptCount++;
			await delay(1000);
		}

		return false;
	};

	const firePublish = async (id: string) => {
		if (isPublishingRef.current) return;
		isPublishingRef.current = true;

		try {
			let resolvedId = id;
			if (id === 'new') {
				const realId = await resolveRealCampaignId();
				if (!realId) {
					onClosePublishingConfirmationModal();
					customToast(
						'Could not save the campaign. Please try again.',
						'error',
					);
					return;
				}
				resolvedId = realId;
			}

			// Fire publish and get callback for polling
			const publishResponse = await publishCampaignChannels(resolvedId, campaign?.placements ?? []);

			// Poll in background — only update modal if still open
			if (publishResponse?.callback) {
				pollPublishResult(publishResponse.callback);
			}
		} catch (error) {
			console.error('Error publishing campaign:', error);
			isPublishingRef.current = false;
			onClosePublishingConfirmationModal();
			customToast(
				'There was an error publishing your campaign. Please try again.',
				'error',
			);
		}
	};

	const pollPublishResult = async (callbackUrl: string) => {
		try {
			const response = await processCallback(callbackUrl, 300);

			const publishedCreatives = response?.output?.publishedCreatives || response?.publishedCreatives || [];
			const failedCreatives = response?.output?.failedCreatives || response?.failedCreatives || [];

			// Server error with no creative results — show friendly error in publishing modal
			if (publishedCreatives.length === 0 && failedCreatives.length === 0 &&
				(response?.status === 'error' || response?.code === 500 || response?.maxAttemptsReached)) {
				setPublishingModalStep('unexpected-error');
				return;
			}

			setPublishResult({ publishedCreatives, failedCreatives });

			// Close publishing modal and open the detailed results modal
			onClosePublishingConfirmationModal();
			onOpenSuccessModal();
		} catch (error) {
			console.error('Error polling publish result:', error);
			setPublishingModalStep('unexpected-error');
		}
	};

	const startLaunch = async () => {
		if (!campaign || !campaignId) return;
		if (isLaunchingRef.current) return;
		isLaunchingRef.current = true;
		setIsPublishButtonLoading(true);

		try {
			const updatedCampaign = { ...campaign, channels: selectedChannels };
			setCampaign(updatedCampaign);

			if (!validateCampaign()) return;

			// Step 1: Show "Checking Credits" modal
			setPublishingModalStep('checking-credits');
			onOpenPublishingConfirmationModal();

			// Resolve real campaign ID early (handles "new" campaigns)
			let resolvedId: any = getCampaignId();
			if (!resolvedId || resolvedId === 'new') {
				resolvedId = await resolveRealCampaignId();
				if (!resolvedId) {
					onClosePublishingConfirmationModal();
					customToast(
						'Could not save the campaign. Please try again.',
						'error',
					);
					return;
				}
			}

			// Check credits before proceeding
			try {
				const creditCheckResponse = await checkCampaignCredits(resolvedId);

				if (!creditCheckResponse.hasEnoughCredits) {
					onClosePublishingConfirmationModal();

					if (creditCheckResponse.errorType === 'CAMPAIGN_LIMIT_REACHED') {
						setCampaignLimitData({
							currentCount: creditCheckResponse.currentCount,
							limit: creditCheckResponse.limit,
							errorMessage: creditCheckResponse.errorMessage,
							provider: creditCheckResponse.provider,
							planName: creditCheckResponse.planName,
							canUpgrade: creditCheckResponse.canUpgrade,
							upgradePrice: creditCheckResponse.upgradePrice,
							upgradeCurrency: creditCheckResponse.upgradeCurrency,
							nextPlanId: creditCheckResponse.nextPlanId,
						});
						onOpenCampaignLimitModal();
						return;
					}

					if (creditCheckResponse.checkoutUrl) {
						setCheckoutUrl(creditCheckResponse.checkoutUrl);
						setAmountRequired(creditCheckResponse.amountRequired || 0);
						onOpenInsufficientCreditsModal();
						return;
					} else {
						customToast('Insufficient credits to launch campaign', 'error');
						return;
					}
				}
			} catch (error) {
				console.error('Error checking credits:', error);
				onClosePublishingConfirmationModal();
				customToast('Error checking credits. Please try again.', 'error');
				return;
			}

			// Step 2: Credits OK — show success briefly
			setPublishingModalStep('credits-ok');
			await delay(1500);

			// Step 3: Transition to "Publishing" step
			setPublishingModalStep('publishing');

			// Ensure all creatives are generated before publishing
			if (!areAllDesignDirectionsWithCreatives() || isCreativesPending) {
				try {
					await genrateCampaignCreatives(campaign, '', true);
					const ready = await waitForCreatives(resolvedId);
					if (!ready) {
						onClosePublishingConfirmationModal();
						customToast(
							'Some creatives are still generating. Please wait and try again.',
							'error',
						);
						return;
					}
				} catch (error) {
					console.error('Error generating creatives:', error);
					onClosePublishingConfirmationModal();
					customToast(
						'Error generating creatives. Please try again.',
						'error',
					);
					return;
				}
			}

			// Update campaign status to "publishing"
			try {
				await createOrUpdateCampaign({ status: 'publishing' }, resolvedId);
				setCampaign({ ...campaign, status: 'publishing' });
			} catch (error) {
				console.error('Error updating campaign status:', error);
			}

			// Fire publish request (modal already visible with publishing step)
			await firePublish(resolvedId);
		} catch (error) {
			console.error('Error in startLaunch:', error);
		} finally {
			isLaunchingRef.current = false;
			// Only re-enable button if publish didn't start (i.e., it was blocked or errored before publishing)
			if (!isPublishingRef.current) {
				setIsPublishButtonLoading(false);
			}
		}
	};

	const fbPageId = integrations?.social?.fb?.attributes?.pagesToPublishContent?.[0]?.id;
	const igName = integrations?.social?.fb?.attributes?.instagramPagesToPublish?.[0]?.name;

	const getSuccessModalTitle = (): 'SUCCESS' | 'ERROR' | 'PARTIAL PUBLISHED' => {
		if (!publishResult) return 'SUCCESS';
		const { publishedCreatives, failedCreatives } = publishResult;
		if (failedCreatives.length === 0) return 'SUCCESS';
		if (publishedCreatives.length === 0) return 'ERROR';
		return 'PARTIAL PUBLISHED';
	};

	return (
		<>
			<PublishingConfirmationModal
				isOpen={isOpenPublishingConfirmationModal}
				onClose={onClosePublishingConfirmationModal}
				step={publishingModalStep}
				publishResult={publishResult}
			/>

			<CampaignSuccessModal
				isOpen={isOpenSuccessModal}
				onClose={onCloseSuccessModal}
				title={getSuccessModalTitle()}
				campaignName={campaign?.title || ''}
				publishedCreatives={publishResult?.publishedCreatives || []}
				failedCreatives={publishResult?.failedCreatives || []}
				selectedChannels={selectedChannels}
				fbPageId={fbPageId}
				igName={igName}
			/>

			<InsufficientCreditsModal
				isOpen={isOpenInsufficientCreditsModal}
				onClose={handleInsufficientCreditsCancel}
				onContinue={handleInsufficientCreditsContinue}
				amountRequired={amountRequired}
			/>

			<CampaignLimitModal
				isOpen={isOpenCampaignLimitModal}
				onClose={onCloseCampaignLimitModal}
				currentCount={campaignLimitData.currentCount}
				limit={campaignLimitData.limit}
				errorMessage={campaignLimitData.errorMessage}
				provider={campaignLimitData.provider}
				planName={campaignLimitData.planName}
				canUpgrade={campaignLimitData.canUpgrade}
				upgradePrice={campaignLimitData.upgradePrice}
				upgradeCurrency={campaignLimitData.upgradeCurrency}
				nextPlanId={campaignLimitData.nextPlanId}
				onUpgradeSuccess={() => {
					startLaunch();
				}}
			/>

			<Container maxW="full" py={6} m={0} px={0}>
				{isCampaignPublishing && (
					<Alert
						status="info"
						borderRadius="md"
						mb={4}
						py={3}
						px={4}
						bg="blue.50"
						alignItems="flex-start"
					>
						<AlertIcon color="blue.500" mt={0.5} />
						<Box>
							<Text fontWeight="semibold" fontSize="sm" color="blue.800">
								Your campaign is being published
							</Text>
							<AlertDescription fontSize="sm" color="blue.700">
								This process usually takes a few minutes. We'll notify you by email once it's ready.
							</AlertDescription>
						</Box>
					</Alert>
				)}
				<Flex justifyContent={{ base: 'stretch', md: 'right' }} alignItems="flex-end">
					<CustomTooltip
						label="Creatives are still being generated. Please wait until they are ready."
						isDisabled={!isDesignDirectionPending && !isCreativesPending}
						hasArrow
						placement="top"
					>
						<Box w={{ base: 'full', md: 'auto' }}>
							<Button
								ref={publishButtonRef}
								data-tour="publish-button"
								variant="orangeSolid"
								_hover={{ opacity: 0.9 }}
								_disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
								onClick={handleSubmit(handlePublishClick)}
								isDisabled={isDesignDirectionPending || isCreativesPending || isPublishButtonLoading || isCampaignPublishing}
								isLoading={isPublishButtonLoading}
								w="full"
							>
								Publish to the channels
							</Button>
						</Box>
					</CustomTooltip>
				</Flex>
			</Container>
		</>
	);
};

export default LaunchCampaign;
